import { useEffect, useMemo, useState } from "react";
import api, { downloadUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FileText, Printer, Eye, BarChart3, Hammer, Wind, Download } from "lucide-react";
import { toast } from "sonner";

/* ============== SOTTO-COMPONENTI ============== */

function PdfPreview({ doc, onClose }) {
    if (!doc) return null;
    const dt = new Date(doc.created_at);
    return (
        <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl bg-white text-slate-900 p-0 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 bg-slate-100 border-b no-print">
                    <div className="text-sm font-semibold">Anteprima documento</div>
                    <Button size="sm" onClick={() => window.print()} data-testid="button-print" className="bg-slate-900 text-white gap-2">
                        <Printer size={14} /> Stampa / PDF
                    </Button>
                </div>
                <div className="print-page p-8 max-h-[80vh] overflow-y-auto">
                    <div className="flex justify-between items-start pb-6 border-b-2 border-slate-900">
                        <div>
                            <div className="text-2xl font-extrabold tracking-tight">PRODUX · Programma produzione</div>
                            <div className="text-xs text-slate-600 mt-1">Distinta di Uscita</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs uppercase tracking-widest text-slate-500">DU</div>
                            <div className="text-2xl font-mono font-bold mt-1">{doc.numero}</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-6 mt-6 text-sm">
                        <div><div className="text-[10px] uppercase text-slate-500 tracking-widest">Data & Ora</div><div className="font-medium">{dt.toLocaleString("it-IT")}</div></div>
                        {doc.destinatario && <div><div className="text-[10px] uppercase text-slate-500 tracking-widest">Destinatario</div><div className="font-medium">{doc.destinatario}</div></div>}
                        {doc.causale && <div><div className="text-[10px] uppercase text-slate-500 tracking-widest">Causale</div><div className="font-medium">{doc.causale}</div></div>}
                    </div>
                    <table className="w-full mt-8 border border-slate-900 text-sm">
                        <thead className="bg-slate-900 text-white text-xs uppercase">
                            <tr>
                                <th className="text-left px-3 py-2">Pedana</th>
                                <th className="text-left px-3 py-2">Lotto</th>
                                <th className="text-left px-3 py-2">Codice</th>
                                <th className="text-left px-3 py-2">Descrizione</th>
                                <th className="text-right px-3 py-2">Cartoni</th>
                                <th className="text-right px-3 py-2">Peso (kg)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(doc.righe || []).map((r, i) => (
                                <tr key={i} className="border-b border-slate-300">
                                    <td className="px-3 py-2 font-mono text-xs">{r.pedana_numero || "-"}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.lotto_numero || "-"}</td>
                                    <td className="px-3 py-2 font-mono">{r.codice}</td>
                                    <td className="px-3 py-2">{r.descrizione}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold">{r.cartoni}</td>
                                    <td className="px-3 py-2 text-right font-mono">{r.peso_kg ?? "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {doc.note && <div className="mt-6 text-sm"><div className="text-[10px] uppercase text-slate-500 tracking-widest">Note</div><div className="mt-1 p-3 bg-slate-50 border border-slate-200 rounded">{doc.note}</div></div>}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DocumentiTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [prev, setPrev] = useState(null);
    useEffect(() => {
        (async () => {
            setLoading(true);
            const { data } = await api.get("/documenti?tipo=uscita");
            setItems(data || []);
            setLoading(false);
        })();
    }, []);
    return (
        <>
            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Numero</th>
                                <th className="text-left px-5 py-3">Data</th>
                                <th className="text-right px-5 py-3">Righe</th>
                                <th className="text-left px-5 py-3">Destinatario</th>
                                <th className="text-left px-5 py-3">Causale</th>
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-docs">
                            {loading && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">
                                    <FileText size={36} className="mx-auto mb-3 opacity-40" />
                                    <div className="text-sm">Nessuna distinta di uscita</div>
                                </td></tr>
                            )}
                            {items.map(d => (
                                <tr key={d.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`row-doc-${d.numero}`}>
                                    <td className="px-5 py-3 doc-code text-xs">{d.numero}</td>
                                    <td className="px-5 py-3 text-slate-300 text-xs">{new Date(d.created_at).toLocaleString("it-IT")}</td>
                                    <td className="px-5 py-3 text-right font-mono text-slate-300">{(d.righe || []).length}</td>
                                    <td className="px-5 py-3 text-slate-200 text-xs font-semibold">{d.destinatario || "—"}</td>
                                    <td className="px-5 py-3 text-slate-400 text-xs">{d.causale || "—"}</td>
                                    <td className="px-5 py-3 text-right">
                                        <button onClick={() => setPrev(d)} data-testid={`button-view-${d.numero}`}
                                            className="p-2 rounded hover:bg-slate-700/60 text-slate-300 mr-1"><Eye size={14} /></button>
                                        <a href={downloadUrl(`/uscita/${d.id}/pdf`)} target="_blank" rel="noreferrer"
                                            data-testid={`button-pdf-${d.numero}`}
                                            title="Apri PDF DU"
                                            className="p-2 inline-block rounded hover:bg-red-500/20 text-red-300"><FileText size={14} /></a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
            <PdfPreview doc={prev} onClose={() => setPrev(null)} />
        </>
    );
}

/* ------------- REPORT ------------- */

function BarChart({ data, label, valueKey, color = "#10b981" }) {
    if (!data || data.length === 0) {
        return <div className="text-xs text-slate-500 italic py-4">Nessun dato nel periodo</div>;
    }
    const max = Math.max(...data.map(d => parseFloat(d[valueKey]) || 0));
    return (
        <div className="space-y-1.5">
            {data.map((d, i) => {
                const v = parseFloat(d[valueKey]) || 0;
                const pct = max > 0 ? (v / max) * 100 : 0;
                return (
                    <div key={i} className="flex items-center gap-2 text-xs">
                        <div className="w-28 truncate text-slate-300 font-mono">{d[label]}</div>
                        <div className="flex-1 bg-slate-800 rounded h-5 overflow-hidden relative">
                            <div className="h-full rounded" style={{ width: `${pct}%`, backgroundColor: color }} />
                            <span className="absolute inset-0 flex items-center px-2 font-mono text-[10px] text-slate-100">{v}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function ReportTab() {
    const todayIso = new Date().toISOString().slice(0, 10);
    const d7 = new Date(); d7.setDate(d7.getDate() - 7);
    const d7Iso = d7.toISOString().slice(0, 10);

    const [reparto, setReparto] = useState("saldatura");
    const [df, setDf] = useState(d7Iso);
    const [dt, setDt] = useState(todayIso);
    const [opId, setOpId] = useState("");
    const [artId, setArtId] = useState("");
    const [mac, setMac] = useState("");
    const [operatori, setOperatori] = useState([]);
    const [articoliSald, setArticoliSald] = useState([]);
    const [articoliEstr, setArticoliEstr] = useState([]);
    const [macchine, setMacchine] = useState([]);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [u, as, ae, mm] = await Promise.all([
                    api.get("/users"),
                    api.get("/articles").catch(() => ({ data: [] })),
                    api.get("/articoli-estrusione").catch(() => ({ data: [] })),
                    api.get("/macchine-estrusione").catch(() => ({ data: [] })),
                ]);
                setOperatori((u.data || []).filter(x => x.role === "operatore" || x.role === "operatore_estrusione"));
                setArticoliSald(as.data || []);
                setArticoliEstr(ae.data || []);
                setMacchine(mm.data || []);
            } catch (e) { /* ignore */ }
        })();
    }, []);

    const fetchReport = async () => {
        setBusy(true);
        try {
            const params = new URLSearchParams();
            if (df) params.set("date_from", df);
            if (dt) params.set("date_to", dt);
            if (opId) params.set("operatore_id", opId);
            if (reparto === "saldatura") {
                if (artId) params.set("article_id", artId);
                const { data } = await api.get(`/reports/saldatura?${params.toString()}`);
                setData(data);
            } else {
                if (artId) params.set("articolo_id", artId);
                if (mac) params.set("macchina_codice", mac);
                const { data } = await api.get(`/reports/estrusione?${params.toString()}`);
                setData(data);
            }
        } catch (e) { toast.error("Errore caricamento report"); }
        finally { setBusy(false); }
    };

    useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, [reparto]);

    const pdfUrl = useMemo(() => {
        const params = new URLSearchParams();
        if (df) params.set("date_from", df);
        if (dt) params.set("date_to", dt);
        if (opId) params.set("operatore_id", opId);
        if (reparto === "saldatura" && artId) params.set("article_id", artId);
        if (reparto === "estrusione" && artId) params.set("articolo_id", artId);
        if (reparto === "estrusione" && mac) params.set("macchina_codice", mac);
        return downloadUrl(`/reports/${reparto}/pdf?${params.toString()}`);
    }, [reparto, df, dt, opId, artId, mac]);

    const unit = reparto === "saldatura" ? "cart." : "kg";
    const totKey = reparto === "saldatura" ? "totale_cartoni" : "totale_kg";
    const valKey = reparto === "saldatura" ? "cartoni" : "kg";

    const operatoriFiltered = useMemo(() => operatori.filter(o =>
        reparto === "saldatura" ? o.role === "operatore" : o.role === "operatore_estrusione"
    ), [operatori, reparto]);

    return (
        <div className="space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" onClick={() => setReparto("saldatura")} data-testid="btn-rep-saldatura"
                    className={reparto === "saldatura" ? "bg-amber-500 text-slate-950 h-9" : "bg-slate-800 text-slate-300 h-9"}>
                    <Hammer size={14} className="mr-1" /> Saldatura
                </Button>
                <Button size="sm" onClick={() => setReparto("estrusione")} data-testid="btn-rep-estrusione"
                    className={reparto === "estrusione" ? "bg-cyan-500 text-slate-950 h-9" : "bg-slate-800 text-slate-300 h-9"}>
                    <Wind size={14} className="mr-1" /> Estrusione
                </Button>
            </div>

            <Card className="card p-4">
                <div className="grid md:grid-cols-5 gap-3 items-end">
                    <div>
                        <Label className="text-[10px] text-slate-400 tracking-widest uppercase">Dal</Label>
                        <Input type="date" value={df} onChange={e => setDf(e.target.value)} data-testid="rep-from" className="bg-slate-950/50 border-slate-800 mt-1" />
                    </div>
                    <div>
                        <Label className="text-[10px] text-slate-400 tracking-widest uppercase">Al</Label>
                        <Input type="date" value={dt} onChange={e => setDt(e.target.value)} data-testid="rep-to" className="bg-slate-950/50 border-slate-800 mt-1" />
                    </div>
                    <div>
                        <Label className="text-[10px] text-slate-400 tracking-widest uppercase">Operatore</Label>
                        <select value={opId} onChange={e => setOpId(e.target.value)} data-testid="rep-op"
                            className="w-full bg-slate-950/50 border border-slate-800 rounded px-2 py-2 mt-1 text-sm">
                            <option value="">Tutti</option>
                            {operatoriFiltered.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] text-slate-400 tracking-widest uppercase">Articolo</Label>
                        <select value={artId} onChange={e => setArtId(e.target.value)} data-testid="rep-art"
                            className="w-full bg-slate-950/50 border border-slate-800 rounded px-2 py-2 mt-1 text-sm">
                            <option value="">Tutti</option>
                            {(reparto === "saldatura" ? articoliSald : articoliEstr).map(a => <option key={a.id} value={a.id}>{a.codice} · {a.descrizione || `${a.larghezza_cm}×${a.spessore_my}μ`}</option>)}
                        </select>
                    </div>
                    {reparto === "estrusione" ? (
                        <div>
                            <Label className="text-[10px] text-slate-400 tracking-widest uppercase">Macchina</Label>
                            <select value={mac} onChange={e => setMac(e.target.value)} data-testid="rep-mac"
                                className="w-full bg-slate-950/50 border border-slate-800 rounded px-2 py-2 mt-1 text-sm">
                                <option value="">Tutte</option>
                                {macchine.map(m => <option key={m.id} value={m.codice}>{m.codice}</option>)}
                            </select>
                        </div>
                    ) : <div></div>}
                </div>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                    <Button size="sm" onClick={fetchReport} disabled={busy} data-testid="btn-rep-apply"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 h-9">
                        {busy ? "Caricamento…" : "Applica filtri"}
                    </Button>
                    <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="btn-rep-pdf">
                        <Button size="sm" variant="outline" className="bg-transparent border-slate-700 text-slate-300 h-9 gap-2">
                            <Download size={14} /> Scarica PDF
                        </Button>
                    </a>
                    {data && (
                        <div className="ml-auto text-xs font-mono text-slate-400">
                            Totale: <b className={reparto === "saldatura" ? "text-amber-300" : "text-cyan-300"}>{data[totKey]} {unit}</b> · {data.rows.length} record
                        </div>
                    )}
                </div>
            </Card>

            {data && (
                <>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Card className="card p-4">
                            <h3 className="font-display font-bold text-sm mb-3 text-slate-200">Per operatore</h3>
                            <BarChart data={data.by_operatore} label="operatore" valueKey={valKey} color={reparto === "saldatura" ? "#f59e0b" : "#06b6d4"} />
                        </Card>
                        {reparto === "estrusione" ? (
                            <Card className="card p-4">
                                <h3 className="font-display font-bold text-sm mb-3 text-slate-200">Per macchina</h3>
                                <BarChart data={data.by_macchina} label="macchina" valueKey="kg" color="#06b6d4" />
                            </Card>
                        ) : (
                            <Card className="card p-4">
                                <h3 className="font-display font-bold text-sm mb-3 text-slate-200">Per giorno</h3>
                                <BarChart data={data.by_giorno} label="data" valueKey={valKey} color="#10b981" />
                            </Card>
                        )}
                    </div>

                    <Card className="card overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
                            <BarChart3 size={14} className="text-slate-400" />
                            <h3 className="font-display font-bold text-sm">Per articolo</h3>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                                <tr>
                                    <th className="text-left px-5 py-2">Codice</th>
                                    <th className="text-left px-5 py-2">Descrizione</th>
                                    {reparto === "estrusione" && <th className="text-right px-5 py-2">Bobine</th>}
                                    <th className="text-right px-5 py-2">{unit.toUpperCase()}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_articolo.length === 0 && <tr><td colSpan={reparto === "estrusione" ? 4 : 3} className="px-5 py-6 text-center text-slate-500 text-xs italic">Nessun dato</td></tr>}
                                {data.by_articolo.map((r, i) => (
                                    <tr key={i} className="border-b border-slate-800/60">
                                        <td className="px-5 py-2 font-mono text-xs text-amber-300">{r.codice}</td>
                                        <td className="px-5 py-2 text-slate-300 text-xs">{r.descrizione}</td>
                                        {reparto === "estrusione" && <td className="px-5 py-2 text-right font-mono text-slate-300">{r.bobine}</td>}
                                        <td className="px-5 py-2 text-right font-mono font-bold text-emerald-300">{r[valKey]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>

                    <Card className="card overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
                            <FileText size={14} className="text-slate-400" />
                            <h3 className="font-display font-bold text-sm">Dettaglio ({data.rows.length} righe)</h3>
                        </div>
                        <div className="overflow-x-auto max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800 sticky top-0 bg-slate-900">
                                    <tr>
                                        <th className="text-left px-5 py-2">{reparto === "saldatura" ? "Lotto" : "Bobina"}</th>
                                        <th className="text-left px-5 py-2">Data</th>
                                        <th className="text-left px-5 py-2">Operatore</th>
                                        {reparto === "estrusione" && <th className="text-left px-5 py-2">Macchina</th>}
                                        <th className="text-left px-5 py-2">Articolo</th>
                                        <th className="text-right px-5 py-2">{unit}</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="rep-rows">
                                    {data.rows.map((r, i) => (
                                        <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                                            <td className="px-5 py-2 doc-code text-xs text-amber-300">{r.lotto || r.bobina}</td>
                                            <td className="px-5 py-2 text-slate-400 text-xs font-mono">{r.data}</td>
                                            <td className="px-5 py-2 text-slate-300 text-xs">{r.operatore}</td>
                                            {reparto === "estrusione" && <td className="px-5 py-2 font-mono text-xs text-slate-400">{r.macchina}</td>}
                                            <td className="px-5 py-2 text-slate-300 text-xs"><span className="font-mono text-amber-300">{r.codice}</span></td>
                                            <td className="px-5 py-2 text-right font-mono font-bold text-emerald-300">{r[valKey]}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}

/* ============== PAGINA PRINCIPALE ============== */

export default function Documenti() {
    const [tab, setTab] = useState("documenti");
    return (
        <div className="space-y-6">
            <div>
                <div className="text-[10px] tracking-[0.3em] font-mono text-amber-500/80">ARCHIVIO</div>
                <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Distinte & Documenti</h1>
                <p className="mt-2 text-slate-400 text-sm">Distinte di uscita (DU) e report produzione per reparto.</p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="tabs-docs" className="bg-slate-900 border border-slate-800">
                    <TabsTrigger value="documenti" data-testid="tab-du"><FileText size={14} className="mr-1" /> Distinte di uscita</TabsTrigger>
                    <TabsTrigger value="report" data-testid="tab-rep"><BarChart3 size={14} className="mr-1" /> Report produzione</TabsTrigger>
                </TabsList>
                <TabsContent value="documenti" className="mt-4"><DocumentiTab /></TabsContent>
                <TabsContent value="report" className="mt-4"><ReportTab /></TabsContent>
            </Tabs>
        </div>
    );
}
